import React, { useCallback } from "react";
import { Box, Typography, IconButton } from "@mui/material";
import { useDropzone } from "react-dropzone";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import FileView from "./FileView";

const AssignmentFileDropZone = ({
    onFilesAdded,
    fileInputRef,
    attachedFiles = [],
    onRemoveFile,
    onOpenModal,
    t,
}) => {
    const onDrop = useCallback(
        (acceptedFiles) => {
            if (acceptedFiles?.length) onFilesAdded(acceptedFiles);
        },
        [onFilesAdded]
    );

    const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
        onDrop,
        noClick: false,
        noKeyboard: true,
        multiple: true,
        accept: {
            "image/*": [".png", ".jpg", ".jpeg", ".gif", ".webp", ".bmp"],
            "application/pdf": [".pdf"],
            "application/msword": [".doc"],
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
            "text/plain": [".txt"],
            "application/vnd.ms-excel": [".xls"],
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
        },
    });

    // Expose open() to parent via ref for programmatic trigger
    React.useEffect(() => {
        if (!fileInputRef) return;
        fileInputRef.current = { open };
        return () => { fileInputRef.current = null; };
    }, [fileInputRef, open]);

    return (
        <Box sx={{ mt: 1.5 }}>
            {/* Drop zone - click or drag to add files */}
            <Box
                {...getRootProps()}
                sx={{
                    border: `2px dashed ${isDragActive ? "#304FFF" : "#ccc"}`,
                    borderRadius: 1,
                    p: 2,
                    textAlign: "center",
                    cursor: "pointer",
                    backgroundColor: isDragActive ? "rgba(48, 79, 255, 0.05)" : "transparent",
                    transition: "all 0.2s ease",
                    "&:hover": { borderColor: "#304FFF", backgroundColor: "rgba(48, 79, 255, 0.03)" },
                }}
            >
                <input {...getInputProps()} style={{ display: "none" }} />
                <InsertDriveFileIcon sx={{ fontSize: 40, color: "#999", mb: 0.5 }} />
                <Typography variant="body2" color="textSecondary">
                    {isDragActive ? t("DropFilesHere") : t("DragDropFilesOrClick")}
                </Typography>
                <IconButton
                    size="small"
                    sx={{ mt: 0.5 }}
                    onClick={(e) => {
                        e.stopPropagation();
                        open();
                    }}
                    title={t("SelectFiles") || "Select files"}
                >
                    <CloudUploadIcon fontSize="small" />
                </IconButton>
            </Box>

            {/* Attached files preview */}
            {attachedFiles?.length > 0 && (
                <Box
                    sx={{
                        display: "flex",
                        gap: 1,
                        flexWrap: "wrap",
                        overflowX: "auto",
                        mt: 1.5,
                        pb: 0.5,
                    }}
                >
                    {attachedFiles.map((file, index) => (
                        <FileView
                            key={`${index}-${file?.name || index}`}
                            file={file}
                            onRemove={() => onRemoveFile(index)}
                            onOpenModal={onOpenModal}
                            isCreate={true}
                        />
                    ))}
                </Box>
            )}
        </Box>
    );
};

export default AssignmentFileDropZone;
